import React, { useState, useContext } from 'react';
import { useAsyncFn } from 'react-use';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { FlexCrate, Notification, Alert } from '@perch-labs/shrike';
import { routes, copyFor } from 'config';
import { NOTIFICATION_DISPLAY_TIME } from 'domain/ux';
import { useAccountContext } from 'contexts/account';
import { BackLink } from 'components/back-link';
import { MAX_SECONDARY_PAGE_WIDTH } from 'domain/style';
import { Page } from 'styles';
import { LoadingPage } from 'components/loading';
import { ErrorPage } from 'components/errors';
import { translateError } from 'utils/errors/translate-error';
import { trackClickEvent } from 'domain/tracking';
import { DeletePaymentMethod } from '../../components/delete-payment-method';
import { MutationVariables } from '../../components/plan-toggles';
import { getPaymentCopy } from '../../payment-methods.utils';
import { SET_PAYMENT_METHOD_ON_PLANS } from '../../payment-methods.api';
import {
  GET_PAYMENT_METHOD,
  GET_PLANS_COUNT_BY_METHOD,
} from './payment-method.api';
import { SaveButton, DeleteButton, StyledSpacer } from './payment-method.style';
import { dividePlansByPaymentMethod } from './payment-method.utils';
import { PlanContent } from './plan-content';

const { paymentWallet } = routes();
const getCopy = copyFor('settings.paymentInformation.paymentMethodPage');

export const PaymentMethod = () => {
  const { paymentMethodId } = useParams<{ paymentMethodId?: string }>();
  const methodId = Number(paymentMethodId);
  const { selectedAccountId } = useAccountContext();
  const { addNotification } = useContext(Notification.Context);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [mutationVariables, setMutationVariables] = useState<
    MutationVariables | undefined
  >();

  const variables = { id: methodId, accountId: selectedAccountId };
  const { data, error, loading } = useQuery<
    GetPaymentMethodQuery,
    GetPaymentMethodQueryVariables
  >(GET_PAYMENT_METHOD, {
    variables,
  });

  const whalePlansCount = useQuery<
    GetPlansCountByMethodQuery,
    GetPlansCountByMethodQueryVariables
  >(GET_PLANS_COUNT_BY_METHOD, {
    variables,
    fetchPolicy: 'network-only', // not refetched for all methods on setPaymentMethodOnPlans
    skip: !data || data?.search.plans.count < 10,
  });

  const [mutate] = useMutation<
    SetPaymentMethodOnPlansMutation,
    SetPaymentMethodOnPlansMutationVariables
  >(SET_PAYMENT_METHOD_ON_PLANS);

  const [mutation, submit] = useAsyncFn(async () => {
    await mutate({
      variables: {
        ...mutationVariables,
        skipDisplayName: true,
      } as SetPaymentMethodOnPlansMutationVariables,
    });
    addNotification({
      displayTime: NOTIFICATION_DISPLAY_TIME,
      message: getCopy('notification'),
      variant: 'success',
    });
  }, [mutate, mutationVariables, addNotification]);

  if (loading || whalePlansCount.loading) return <LoadingPage />;
  if (error || whalePlansCount.error || !data?.paymentMethod)
    return <ErrorPage />;

  const { paymentMethod, search } = data;
  const { plansWithOtherMethod, plansWithThisAssignedMethod } =
    dividePlansByPaymentMethod({
      plans: search.plans.plans,
      paymentMethodId: methodId,
    });
  const whaleAssignedPlansCount = whalePlansCount.data?.search.plans.count;
  const paymentMethodInUse =
    !!search.plans.plans.find(plan => plan.paymentMethodId === methodId) ||
    !!whaleAssignedPlansCount;
  const { label, text } = getPaymentCopy({
    paymentMethod,
    labelProps: { textStyle: 'heading800', tag: 'h1' },
  });

  return (
    <>
      <BackLink to={paymentWallet} />
      <Page maxWidth={MAX_SECONDARY_PAGE_WIDTH}>
        <FlexCrate alignItems="start" flexDirection="column">
          {label}
          {text}
          <StyledSpacer />
          <PlanContent
            plansWithThisAssignedMethod={plansWithThisAssignedMethod}
            plansWithOtherMethod={plansWithOtherMethod}
            setMutationVariables={setMutationVariables}
            paymentMethodId={methodId}
            whaleAssignedPlansCount={whaleAssignedPlansCount}
          />
          {!!plansWithOtherMethod.length && (
            <>
              {mutation.error && (
                <Alert margin={{ top: '8px' }}>
                  {translateError(mutation.error)}
                </Alert>
              )}
              <SaveButton
                onClick={() => {
                  submit();
                  trackClickEvent(
                    'paymentInformation.paymentMethod.saveChanges'
                  );
                }}
                disabled={!mutationVariables}
                loading={mutation.loading}
              >
                {getCopy('buttons.save')}
              </SaveButton>
            </>
          )}
          <DeleteButton
            onClick={() => {
              setShowDeleteModal(true);
              trackClickEvent('paymentInformation.paymentMethod.remove');
            }}
          >
            {getCopy('buttons.remove')}
          </DeleteButton>
        </FlexCrate>
      </Page>
      {showDeleteModal && (
        <DeletePaymentMethod
          close={() => setShowDeleteModal(false)}
          paymentMethodInUse={paymentMethodInUse}
          paymentMethodId={methodId}
        />
      )}
    </>
  );
};
